// TARA Specialty Landing Page — Editorial redesign
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/Header/Header';
import ScrollToTopButton from '../../../components/ScrollToTopButton/ScrollToTopButton';
import SeoMeta from '../../../components/SeoMeta/SeoMeta';
import TaraEditorialFooter from './TaraEditorialFooter';
import './TaraLandingPage.css';

import { getBaseApiUrl, getEduPortalUrl } from '../../../config/api';
import { checkoutRefusalMessage, showCheckoutRefusal } from '../../checkout/checkoutRefusal';
import { ORG_PLAN_NOTICE, useStoredUserMayPurchase } from '../../checkout/purchaseEligibility';

// Reusable horizontal scroll-carousel shell (matches the swipeable card
// pattern used across the admissions matrix, method cards, sample
// question and school case-study sections).
function ScrollCarousel({ children, ariaLabel }) {
  const scrollRef = useRef(null);
  const [dotIndex, setDotIndex] = useState(0);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const count = React.Children.count(children);

  const cardStep = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !el.firstChild || !el.firstChild.firstElementChild) return 0;
    const card = el.firstChild.firstElementChild;
    return card.getBoundingClientRect().width + 14;
  }, []);

  const updateUI = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollLeft = el.scrollLeft;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const step = cardStep();
    const activeIndex = step ? Math.round(scrollLeft / step) : 0;
    setDotIndex(activeIndex);
    setShowPrev(scrollLeft > 4);
    setShowNext(scrollLeft < maxScroll - 4);
  }, [cardStep]);

  useEffect(() => {
    updateUI();
    const el = scrollRef.current;
    if (!el) return undefined;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { updateUI(); ticking = false; });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateUI);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateUI);
    };
  }, [updateUI]);

  const scrollByStep = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * cardStep(), behavior: 'smooth' });
  };

  return (
    <div className="tara-scroll-carousel">
      <button
        type="button"
        className={`tara-carousel-arrow tara-arrow-prev ${showPrev ? 'show' : ''}`}
        onClick={() => scrollByStep(-1)}
        aria-label={`Previous ${ariaLabel}`}
      >
        ←
      </button>
      <button
        type="button"
        className={`tara-carousel-arrow tara-arrow-next ${showNext ? 'show' : ''}`}
        onClick={() => scrollByStep(1)}
        aria-label={`Next ${ariaLabel}`}
      >
        →
      </button>
      <div className="tara-card-scroll" ref={scrollRef}>
        <div className="tara-card-track">
          {children}
        </div>
      </div>
      <div className="tara-swipe-dots">
        {Array.from({ length: count }).map((_, i) => (
          <span key={i} className={i === dotIndex ? 'active' : ''} />
        ))}
      </div>
    </div>
  );
}

export default function TaraLandingPage() {
  const navigate = useNavigate();
  const mayPurchase = useStoredUserMayPurchase();
  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const handleExplorePlans = () => {
    const el = document.getElementById('pricing') || document.getElementById('tara-pricing');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubscribe = async (tierName) => {
    const tierSlug = tierName.toLowerCase().includes('beyond') ? 'tara_beyond' : (tierName.toLowerCase().includes('foundation') ? 'tara_foundations' : 'tara_beginnings');

    const token = localStorage.getItem('pathbuddy_access_token') || sessionStorage.getItem('pathbuddy_access_token') || localStorage.getItem('token');
    const userRaw = localStorage.getItem('pathbuddy_user') || sessionStorage.getItem('pathbuddy_user') || localStorage.getItem('user');
    const user = (token && userRaw) ? (typeof userRaw === 'string' && userRaw.startsWith('{') ? JSON.parse(userRaw) : { email: userRaw }) : null;

    if (!user || !user.email) {
      sessionStorage.setItem('pending_checkout_tier', tierSlug);
      sessionStorage.setItem('pending_checkout_exam', 'tara');
      navigate(`/login?signup=true&tier=${encodeURIComponent(tierSlug)}&exam=tara`);
      return;
    }

    if (tierSlug === 'tara_beginnings') {
      window.location.href = getEduPortalUrl('/practice/tara');
      return;
    }

    try {
      const apiUrl = getBaseApiUrl();
      const token2 = localStorage.getItem('pathbuddy_access_token') || sessionStorage.getItem('pathbuddy_access_token') || localStorage.getItem('token');

      const headers = { 'Content-Type': 'application/json' };
      if (token2) {
        headers['Authorization'] = `Bearer ${token2}`;
      }

      const res = await fetch(`${apiUrl}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          tier_name: tierSlug,
          exam_type: 'tara',
          user_id: user?.id,
          email: user?.email
        })
      });
      const data = await res.json().catch(() => ({}));
      const refusal = checkoutRefusalMessage(res, data);
      if (refusal) {
        showCheckoutRefusal(refusal);
        return;
      }
      if (data.checkoutUrl || data.url) {
        window.location.href = data.checkoutUrl || data.url;
      } else {
        sessionStorage.setItem('pending_checkout_tier', tierSlug);
        sessionStorage.setItem('pending_checkout_exam', 'tara');
        navigate(`/login?signup=true&tier=${encodeURIComponent(tierSlug)}&exam=tara`);
      }
    } catch (err) {
      sessionStorage.setItem('pending_checkout_tier', tierSlug);
      sessionStorage.setItem('pending_checkout_exam', 'tara');
      navigate(`/login?signup=true&tier=${encodeURIComponent(tierSlug)}&exam=tara`);
    }
  };

  const taraRequirements = [
    { uni: "University of Oxford", course: "PPE / Economics & Management", requirement: "Mandatory", target: "68+ / 100", details: "Primary test for candidate shortlisting" },
    { uni: "University of Cambridge", course: "Human, Social & Political Sciences", requirement: "Mandatory", target: "65+ / 100", details: "Used for interview & assessment ranking" },
    { uni: "London School of Economics (LSE)", course: "BSc Politics & Economics", requirement: "Mandatory", target: "64+ / 100", details: "Weighted in competitive applicant review" },
    { uni: "Imperial College London", course: "BSc Business & Technology", requirement: "Mandatory", target: "65+ / 100", details: "Evaluates quantitative analytical reasoning" },
    { uni: "King's College London (KCL)", course: "BSc Political Economy", requirement: "Mandatory", target: "62+ / 100", details: "Holistic score evaluation" },
    { uni: "Durham University", course: "BA Philosophy, Politics & Economics", requirement: "Recommended", target: "60+ / 100", details: "Unlocks alternative contextual offer" },
    { uni: "University of Warwick", course: "BA Economics & Global Affairs", requirement: "Recommended", target: "62+ / 100", details: "Strengthens competitive application ranking" },
    { uni: "University of Bristol", course: "BSc Politics & International Relations", requirement: "Recommended", target: "60+ / 100", details: "Used for offer grade reduction" },
    { uni: "SOAS University of London", course: "BA International Development", requirement: "Recommended", target: "58+ / 100", details: "Analytical skill verification" },
    { uni: "University of St Andrews", course: "MA Sustainable Development", requirement: "Recommended", target: "60+ / 100", details: "Secondary analytical benchmark" }
  ];

  const structureCards = [
    {
      tag: "Method",
      title: "The IVORI Method",
      meta: "Interact with Pedagogical AI",
      items: [
        "Trained across three foundations spanning argumentation theory, decision science and writing-process research, so it reasons through Critical Thinking, Problem Solving and the Writing Task the way an Oxbridge tutor would",
        "Finds exactly where an inference, a calculation route or a plan stops holding up, so you correct the actual reasoning gap, not just the final answer",
        <>The world's first <button type="button" className="tara-inline-link tara-link-btn" onClick={() => navigate('/method/tara')}>pedagogical AI model</button>, built on the first <a href="https://ivorilab.com" target="_blank" rel="noopener noreferrer" className="tara-inline-link">Foundation Decision World Model</a>, meaning the feedback comes from a system built for reasoning</>
      ]
    },
    {
      tag: "Three Modules, One Discipline",
      title: "Three Modules, Three Traditions",
      meta: "Trained on All Three Deliberately",
      items: [
        "Critical Thinking is trained on Walton's argumentation schemes — testing whether an argument's underlying pattern actually survives its own critical question",
        "Problem Solving is trained on Frederick's cognitive reflection research — building the reflective route past the fast, tempting wrong answer",
        "The Writing Task is trained on Hayes and Flower's cognitive process model of writing — planning what an essay needs to explore before translating a first-draft verdict onto the page"
      ]
    },
    {
      tag: "Foundation I",
      title: "Walton's Argumentation Schemes",
      meta: "Critical Thinking — 40 min / 22 Q",
      items: [
        "Diagnoses which recurring pattern of reasoning — argument from correlation, from analogy, from expert opinion — a passage is actually running, then tests whether its specific critical question holds",
        "Trains the habit of asking whether a stated mechanism actually operates, exactly the gap the minimum-wage sample question in this guide's carousel turns on",
        "Diagnostics tag a miss by which critical question was never asked, not by the passage's topic"
      ]
    },
    {
      tag: "Foundation II",
      title: "Frederick's Cognitive Reflection",
      meta: "Problem Solving — 40 min / 22 Q",
      items: [
        "Trains recognizing the fast, intuitive answer a ratio question tempts you toward, and the slower reflective route that actually resolves it",
        "Maps directly onto the Economics/PPE/Politics offers question in this guide's carousel — jumping to 45 as the answer is the trap; setting up the part-value equation is the reflective route",
        "Diagnostics flag when a correct answer was reached by the fast route rather than the reflective one, since the fast route won't generalize to the next question"
      ]
    },
    {
      tag: "Foundation III",
      title: "Hayes and Flower's Writing Process Model",
      meta: "The Writing Task — 750 words / 1 of 3 prompts",
      items: [
        "Separates planning an essay's position from translating it onto the page, so a candidate revises the plan, not just the prose, when a paragraph isn't working",
        "Trains exploring which conditions cut which way before committing to a blanket verdict — the gap between a merely competent essay and an Elite-level one",
        "Feedback flags a planning failure specifically, rather than a vague note to \"add more nuance\""
      ]
    }
  ];

  const schoolCaseStudies = [
    {
      name: "Handsworth Grammar",
      contact: "Dom Bird · Deputy Head Teacher",
      body: "King Edward VI Handsworth Grammar School for Boys has used IVORI for almost two years, supporting Year 12 with admissions test preparation — including the TARA — case study analysis, interview practice and UCAS personal statement support.",
      detailLabel: "Partner Since",
      detailValue: "May 2024",
      detail2Label: "License",
      detail2Value: "Annual"
    },
    {
      name: "Clifton College",
      contact: "Clifton's Team · Careers Department",
      body: "Piloted IVORI with Year 12 students transitioning into Year 13, preparing them for admissions tests and interviews across the full application cycle — with particular focus on the PPE and Economics cohort sitting the TARA.",
      detailLabel: "Cohort",
      detailValue: "Year 12 → Year 13",
      detail2Label: "Focus",
      detail2Value: "Oxbridge Admissions"
    },
    {
      name: "King Edward VI",
      contact: "Active Discussions",
      body: "Building on the existing relationship with Handsworth Grammar, IVORI is in active discussions with the wider King Edward VI Foundation about extending TARA preparation across its family of Birmingham grammar schools.",
      detailLabel: "Status",
      detailValue: "In Discussion",
      detail2Label: "Scope",
      detail2Value: "Foundation-Wide"
    },
    {
      name: "Grammar Schools",
      contact: "School Group",
      body: "Deployed across schools within the grammar school group for TARA practice, supporting Year 12 and 13 students preparing for PPE, Economics and Politics admissions tests and Oxbridge interviews.",
      detailLabel: "Group",
      detailValue: "State Grammar Schools",
      detail2Label: "Focus",
      detail2Value: "TARA Practice"
    },
    {
      name: "Independent Schools",
      contact: "School Group",
      body: "Deployed across schools within the independent school group for TARA practice, supporting Year 12 and 13 students preparing for PPE, Economics and Politics admissions tests and Oxbridge interviews.",
      detailLabel: "Group",
      detailValue: "Independent Schools",
      detail2Label: "Focus",
      detail2Value: "TARA Practice"
    }
  ];

  const faqs = [
    {
      q: "Which universities require the TARA for 2026/2027 entry, and what does it test?",
      a: [
        "The TARA (Test of Academic Reasoning Assessment), which replaced the discontinued TSA, is used by a number of leading UK universities for PPE, Economics, Politics and related degrees — Oxford, Cambridge, LSE, Imperial and KCL as mandatory requirements, and Durham, Warwick, Bristol, SOAS and St Andrews as recommended benchmarks.",
        "It tests critical thinking, problem solving and writing across three modules, not subject knowledge. You do not need prior study of economics or politics to take it."
      ]
    },
    {
      q: "What is IVORI's pedagogical method for TARA, and who's behind it?",
      a: [
        "Every module, question and mark scheme is reviewed by admissions specialists and academics across politics, economics and social sciences, alongside educators with formal pedagogical training. That review process is what lets IVORI explain not just the right answer, but why every alternative fails.",
        "IVORI trains on three research traditions, one per module: Walton's argumentation schemes for Critical Thinking, Frederick's cognitive reflection research for Problem Solving, and Hayes and Flower's cognitive process model of writing for the Writing Task. Underneath all three sits IVORI's own foundation decision world model — proprietary technology researched and pioneered in-house by IVORI Lab, and, we believe, the world's first pedagogical AI model built on this kind of foundation.",
        "It's IVORI Lab's proprietary foundation decision world model — a system trained to reason about decision-making under uncertainty, rather than simply predict text. IVORI's TARA products sit on top of this underlying model, using its proprietary training methodology to evaluate arguments, check calculation routes and grade essays the way a human tutor would."
      ]
    },
    {
      q: "How does IVORI's TARA practice, grading and scoring work?",
      a: [
        "Critical Thinking and Problem Solving are each 40 minutes with 22 multiple-choice questions, taken by all candidates. The Writing Task gives a 750-word limit and a choice of three prompts, with only one answered.",
        "Submit your essay and IVORI evaluates it against real admissions criteria, with structured feedback showing precisely what separates a competent answer from an Elite-level judgement — usually the difference between a blanket verdict and an argument that identifies which conditions cut which way.",
        "Full mocks reproduce the TARA's exact timing across all three modules, so pacing becomes automatic rather than something you're tracking mid-exam."
      ]
    },
    {
      q: "Can I use IVORI for more than just TARA preparation?",
      a: ["Yes. IVORI's reasoning practice is designed around transferable skills including argument evaluation, quantitative problem solving and structured essay writing. TARA is one of several admissions preparation products within the IVORI ecosystem."]
    },
    {
      q: "Can I cancel my subscription at any time?",
      a: ["Yes. All subscriptions can be managed or cancelled in one click from your account profile with zero hidden fees or commitments."]
    }
  ];

  const host = window.location.hostname.toLowerCase();
  const onIvoriEdu = host === 'tara.ivoriedu.com';
  const onIvoriAiEdu = host === 'tara.edu.ivoriai.com';
  const taraUrl = onIvoriEdu
    ? 'https://tara.ivoriedu.com/'
    : onIvoriAiEdu
      ? 'https://tara.edu.ivoriai.com/'
      : (host.includes('tara') ? 'https://tara.edu.ivoriai.com/' : 'https://ivoriai.com/edu/tara');
  const eduHomeUrl = onIvoriEdu
    ? 'https://ivoriedu.com'
    : 'https://ivoriai.com/';

  return (
    <div className="tara-landing-root">
      <SeoMeta
        title="TARA Exam Prep 2026 - Thinking & Aptitude Reasoning Assessment"
        description="Master the TARA (Thinking & Aptitude Reasoning Assessment) for Oxford, Cambridge, LSE, Imperial, and KCL with ivori realistic computer mock tests and critical thinking breakdowns."
        keywords="TARA exam prep, TARA practice test, Oxford TARA, Cambridge TARA, LSE reasoning test, critical thinking prep, ivori tara"
        url={taraUrl}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Course",
                "name": "TARA Test of Academic Reasoning Assessment Preparation",
                "description": "Master the TARA for Oxford, Cambridge, LSE, Imperial, and KCL with ivori timed computer-based exam simulations across Critical Thinking, Problem Solving and the Writing Task.",
                "provider": { "@type": "Organization", "name": "ivori", "url": eduHomeUrl },
                "url": taraUrl,
                "courseMode": "online",
                "inLanguage": "en-GB",
                "isAccessibleForFree": false,
                "educationalLevel": "A-Level / Undergraduate Admissions",
                "teaches": [
                  "Critical Thinking & Argument Evaluation",
                  "Problem Solving & Cognitive Reflection",
                  "Writing Task Planning & Structured Argumentation"
                ]
              },
              {
                "@type": "FAQPage",
                "mainEntity": faqs.map((f) => ({
                  "@type": "Question",
                  "name": f.q,
                  "acceptedAnswer": { "@type": "Answer", "text": f.a.join(' ') }
                }))
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ivoriai.com" },
                  { "@type": "ListItem", "position": 2, "name": "TARA Preparation", "item": taraUrl }
                ]
              }
            ]
          })
        }}
      />
      <Header variant="landing" />

      {/* HERO */}
      <section className="tara-hero-section">
        <div className="tara-container tara-hero-content">
          <span className="tara-mono-eyebrow">IVORI Edu</span>
          <h1 className="tara-hero-title">Master the TARA.</h1>
          <p className="tara-hero-stat">80% of IVORI students score 65+ / 100 in their strongest module.</p>
          <p className="tara-hero-tagline">Train the reasoning every TARA module rewards.</p>
          <button type="button" className="tara-hero-cta" onClick={handleExplorePlans}>
            Choose Tier
          </button>
        </div>
      </section>

      {/* ADMISSIONS GUIDE */}
      <section id="tara-matrix" className="tara-editorial-section">
        <div className="tara-container">
          <div className="tara-editorial-section-head">
            <span className="tara-mono-eyebrow">Target Scores</span>
            <h2>UK University TARA Admissions Matrix for 2026/2027 Entry</h2>
            <p>Benchmark against Oxbridge, LSE and other leading UK universities offering PPE, Economics and Politics</p>
          </div>

          <ScrollCarousel ariaLabel="university">
            {taraRequirements.map((row, idx) => (
              <div className="tara-matrix-card" key={idx}>
                <div className="tara-matrix-uni">{row.uni}</div>
                <div className="tara-matrix-row"><span>Degree Program</span><b>{row.course}</b></div>
                <div className="tara-matrix-row"><span>Requirement</span><b className="tara-req-badge">{row.requirement}</b></div>
                <div className="tara-matrix-row tara-score-cell"><span>Target Score</span><b>{row.target}</b></div>
                <div className="tara-matrix-row"><span>Admissions Note</span><b>{row.details}</b></div>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* HOW IT WORKS / IVORI METHOD */}
      <section className="tara-editorial-section">
        <div className="tara-container">
          <div className="tara-editorial-section-head">
            <span className="tara-mono-eyebrow">How It Works</span>
            <h2>Utilize IVORI Edu</h2>
            <p>Every candidate sits Critical Thinking, Problem Solving and the Writing Task — trained across three foundations built for exactly that split.</p>
          </div>

          <ScrollCarousel ariaLabel="module">
            {structureCards.map((card, idx) => (
              <div className="tara-structure-card" key={idx}>
                <span className="tara-mono-tag">{card.tag}</span>
                <h3>{card.title}</h3>
                <div className="tara-structure-meta">{card.meta}</div>
                <ul>
                  {card.items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* SAMPLE QUESTION */}
      <section className="tara-editorial-section">
        <div className="tara-container">
          <div className="tara-editorial-section-head">
            <span className="tara-mono-eyebrow">Interactive Practice</span>
            <h2>Improve Your Score</h2>
            <p>See how IVORI explains a Critical Thinking question, solves a Problem Solving question, and grades a Writing Task essay</p>
          </div>

          <ScrollCarousel ariaLabel="card">
            <div className="tara-qcard-panel">
              <span className="tara-mono-tag">Critical Thinking</span>
              <div className="tara-passage">
                "Governments that raise the minimum wage are often praised for reducing in-work poverty. Yet if firms respond to higher wage costs by cutting jobs or hours, the policy's beneficiaries are not low-wage workers as a class, but specifically those who keep their jobs at the new, higher rate — a different and often smaller group than the one the policy was designed to help."
              </div>
              <h3>Which of the following, if true, would most weaken the passage's argument?</h3>
            </div>

            <div className="tara-qcard-panel">
              <div className="tara-option correct">A) Studies show minimum wage increases have historically had minimal impact on employment levels.</div>
              <div className="tara-option">B) The minority of workers who lose their jobs are eligible for unemployment benefits.</div>
              <div className="tara-option">C) The policy also affects some workers who were not previously earning minimum wage.</div>
              <div className="tara-option">D) Firms tend to raise prices in response to increased wage costs.</div>
            </div>

            <div className="tara-qcard-panel">
              <span className="tara-mono-tag">Explanation</span>
              <p>The argument depends entirely on the assumption that firms respond to higher wages by cutting jobs or hours — the mechanism the whole conclusion rests on.</p>
              <p>Option A removes that assumption directly: if the employment effect is minimal, the "different, smaller group" conclusion no longer follows.</p>
              <p>Options B, C and D are all true-sounding side effects, but none of them touch the actual mechanism the argument relies on.</p>
            </div>

            <div className="tara-qcard-panel">
              <span className="tara-mono-tag">Problem Solving</span>
              <h3>A university offers places across three programmes in the ratio 5:3:2 (Economics : PPE : Politics). If Economics received 45 more offers than Politics, what is the total number of offers made across all three programmes?</h3>
            </div>

            <div className="tara-qcard-panel">
              <div className="tara-option">A) 100</div>
              <div className="tara-option">B) 120</div>
              <div className="tara-option correct">C) 150</div>
              <div className="tara-option">D) 180</div>
            </div>

            <div className="tara-qcard-panel">
              <span className="tara-mono-tag">Explanation</span>
              <p>Economics minus Politics corresponds to 5 − 2 = 3 parts of the ratio, and this equals 45, so one part = 15.</p>
              <p>The three programmes together make up 5 + 3 + 2 = 10 parts, so the total is 10 × 15 = 150.</p>
            </div>

            <div className="tara-qcard-panel">
              <span className="tara-mono-tag">Writing Task</span>
              <div className="tara-option">Political transparency weakens effective government.</div>
              <div className="tara-option">Scientific progress is impossible without freedom of inquiry.</div>
              <div className="tara-option">"Freedom is therefore always also a problem of defining a dangerous excess." — Jef Huysmans</div>
            </div>

            <div className="tara-qcard-panel">
              <span className="tara-mono-tag">Student Essay Extract</span>
              <div className="tara-passage">
                "However, this does not mean that secrecy generally produces better government. A government protected from scrutiny may make decisions more quickly, but it may also make worse decisions because officials face weaker incentives to justify their reasoning or expose mistakes. The important question is therefore not simply whether transparency helps or hinders government, but whether the benefits of scrutiny outweigh the constraints that scrutiny places on decision-making."
              </div>
            </div>

            <div className="tara-qcard-panel">
              <span className="tara-mono-tag">Feedback</span>
              <p>To reach an Elite-level judgement, the essay should move beyond deciding whether transparency is generally good or bad. It should identify which kinds of transparency, at which stages of decision-making, and under which political conditions strengthen or weaken effective government.</p>
              <p>The strongest response would also question whether transparency has the same effect at every stage of government. This creates a more difficult possibility: some transparency may improve government while complete transparency weakens it.</p>
            </div>
          </ScrollCarousel>
        </div>
      </section>

      {/* SCHOOL CASE STUDIES */}
      <section className="tara-editorial-section">
        <div className="tara-container">
          <div className="tara-editorial-section-head">
            <span className="tara-mono-eyebrow">Used in Schools</span>
            <h2>Trusted by UK Schools</h2>
            <p>Deployed across top UK grammar and independent schools with TARA cohorts</p>
          </div>

          <ScrollCarousel ariaLabel="school">
            {schoolCaseStudies.map((school, idx) => (
              <div className="tara-school-card" key={idx}>
                <h3>{school.name}</h3>
                <div className="tara-school-contact">{school.contact}</div>
                <p>{school.body}</p>
                <p className="tara-school-more">
                  Find out more about{' '}
                  <button type="button" className="tara-inline-link tara-link-btn" onClick={() => navigate('/cases')}>
                    {school.name}
                  </button>
                </p>
                <div className="tara-school-detail">
                  {school.detailLabel}: <b>{school.detailValue}</b> · {school.detail2Label}: <b>{school.detail2Value}</b>
                </div>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="tara-editorial-section tara-pricing-editorial">
        <div className="tara-container">
          <div className="tara-editorial-section-head">
            <span className="tara-mono-eyebrow">Prep Tiers</span>
            <h2>Select Your Tier</h2>
            <p>Choose your level and go deeper when you're ready to improve your score</p>
            {!mayPurchase && <p>{ORG_PLAN_NOTICE}</p>}
          </div>

          <div className="tara-pricing-grid">
            <div className="tara-plan">
              <h3>Beginnings</h3>
              <div className="tara-plan-price">
                <span className="amount">£0</span> <span className="period">free forever</span>
              </div>
              <button type="button" className="tara-plan-btn" onClick={() => handleSubscribe('TARA Beginnings')}>
                Get Started
              </button>
              <ul>
                <li><span className="tara-check">·</span> <b>All Content for Free</b></li>
                <li><span className="tara-check">·</span> 4 Official Tests / 182 Questions</li>
                <li><span className="tara-check">·</span> 19 Easy Tests / 855 Questions</li>
                <li><span className="tara-check">·</span> 350+ Argumentation &amp; Case Analysis Tutorials</li>
              </ul>
            </div>

            <div className="tara-plan">
              <h3>Foundations</h3>
              <div className="tara-plan-price">
                <span className="amount">£60</span> <span className="period">12-month access</span>
              </div>
              {mayPurchase && (
                <button type="button" className="tara-plan-btn" onClick={() => handleSubscribe('TARA Foundations')}>
                  Get Started
                </button>
              )}
              <ul>
                <li><span className="tara-check">·</span> <b>All content in Beginnings</b></li>
                <li><span className="tara-check">·</span> 12 Standard Tests / 540 Questions</li>
                <li><span className="tara-check">·</span> Writing Task Essay Grading &amp; Feedback Suite</li>
                <li><span className="tara-check">·</span> UCAS Personal Statement Reviewer</li>
              </ul>
            </div>

            <div className="tara-plan featured">
              <span className="tara-plan-badge">Recommended · Oxbridge</span>
              <h3>Beyond</h3>
              <div className="tara-plan-price">
                <span className="amount">£80</span> <span className="period">12-month access</span>
              </div>
              {mayPurchase && (
                <button type="button" className="tara-plan-btn" onClick={() => handleSubscribe('TARA Beyond')}>
                  Get Started
                </button>
              )}
              <ul>
                <li><span className="tara-check">·</span> <b>All content in Foundations</b></li>
                <li><span className="tara-check">·</span> 4 Hard Tests / 180 Questions</li>
                <li><span className="tara-check">·</span> 300+ Critical Reasoning &amp; Problem Solving Question Bank</li>
                <li><span className="tara-check">·</span> 16 Oxbridge Interview Simulations</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="tara-editorial-section tara-faq-editorial">
        <div className="tara-container">
          <div className="tara-editorial-section-head">
            <span className="tara-mono-eyebrow">Got Questions?</span>
            <h2>FAQs</h2>
            <p>Find answers on prep tiers, scoring and how IVORI's feedback works</p>
          </div>

          <div className="tara-faq">
            {faqs.map((faq, index) => (
              <div key={index} className={`tara-faq-item ${activeFaq === index ? 'open' : ''}`}>
                <div className="tara-faq-q" onClick={() => toggleFaq(index)}>
                  <span>{faq.q}</span>
                  <span className="tara-faq-plus">+</span>
                </div>
                <div className="tara-faq-a">
                  {faq.a.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TaraEditorialFooter />
      <ScrollToTopButton />
    </div>
  );
}